import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEdit, faTrash, faPlus, faSearch, faTimes, faPlay, faPause, faCopy, faRobot, faCheckCircle, faClock } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import ChatbotAPI from './components/ChatbotAPI';
import ChatbotFlowEditor from './components/ChatbotFlowEditor';
import Swal from 'sweetalert2';
import Preloader from '../preloader';
import { ErrorToastContainer, SuccessToastContainer } from '../../function/CustomFunction';
import './ManageChatbots.css';

function ManageChatbots() {
  const navigate = useNavigate();
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [loadingChatbotId, setLoadingChatbotId] = useState(null);
  const [showChatbotDetails, setShowChatbotDetails] = useState(false);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [showFlowEditor, setShowFlowEditor] = useState(false);
  const [editingChatbotId, setEditingChatbotId] = useState(null);

  // Load chatbots on component mount
  useEffect(() => {
    loadChatbots();
  }, []);

  const loadChatbots = async () => {
    setLoading(true);
    try {
      const chatbotsData = await ChatbotAPI.getAllChatbots();
      console.log('Loaded chatbots data:', chatbotsData);
      if (Array.isArray(chatbotsData)) {
        setChatbots(chatbotsData);
      } else {
        console.error('Invalid chatbots data format:', chatbotsData);
        setChatbots([]);
      }
    } catch (error) {
      console.error('Error loading chatbots:', error);
      ErrorToastContainer('Failed to load chatbots');
      setChatbots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChatbot = async (chatbotId, chatbotName) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${chatbotName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setLoadingChatbotId(chatbotId);
      try {
        const success = await ChatbotAPI.deleteChatbot(chatbotId);
        if (success) {
          setChatbots(prevChatbots => prevChatbots.filter(chatbot => chatbot.id !== chatbotId));
          SuccessToastContainer('Chatbot deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting chatbot:', error);
        ErrorToastContainer('Failed to delete chatbot');
      } finally {
        setLoadingChatbotId(null);
      }
    }
  };

  const handleToggleStatus = async (chatbotId, currentStatus) => {
    try {
      const chatbot = chatbots.find(c => c.id === chatbotId);
      if (!chatbot) return;

      const updatedData = {
        ...chatbot,
        isactive: currentStatus === 1 ? 0 : 1
      };

      const result = await ChatbotAPI.updateChatbot(chatbotId, updatedData);
      if (result) {
        setChatbots(prevChatbots => 
          prevChatbots.map(c => 
            c.id === chatbotId 
              ? { ...c, isactive: currentStatus === 1 ? 0 : 1 }
              : c
          )
        );
        SuccessToastContainer(`Chatbot ${currentStatus === 1 ? 'deactivated' : 'activated'} successfully!`);
      }
    } catch (error) {
      console.error('Error toggling chatbot status:', error);
      ErrorToastContainer('Failed to update chatbot status');
    }
  };

  const handleDuplicateChatbot = async (chatbot) => {
    try {
      const duplicatedData = {
        ...chatbot,
        chatbot_name: `${chatbot.chatbot_name} (Copy)`,
        isactive: 0 // Start as inactive
      };
      delete duplicatedData.id; // Remove ID to create new chatbot
      delete duplicatedData.created_at;
      delete duplicatedData.updated_at;

      const result = await ChatbotAPI.createChatbot(duplicatedData);
      if (result) {
        setChatbots(prevChatbots => [...prevChatbots, result]);
        SuccessToastContainer('Chatbot duplicated successfully!');
      }
    } catch (error) {
      console.error('Error duplicating chatbot:', error);
      ErrorToastContainer('Failed to duplicate chatbot');
    }
  };

  const handleViewDetails = (chatbot) => {
    setSelectedChatbot(chatbot);
    setShowChatbotDetails(true);
  };

  const handleEditChatbot = (chatbotId) => {
    setEditingChatbotId(chatbotId);
    setShowFlowEditor(true);
  };

  const handleCreateNew = () => {
    navigate('/client-area/chat-bot');
  };

  const handleFlowEditorClose = () => {
    setShowFlowEditor(false);
    setEditingChatbotId(null);
  };

  const handleFlowEditorSave = (updatedChatbot) => {
    // Update the chatbot in the list
    setChatbots(prevChatbots => 
      prevChatbots.map(c => 
        c.id === updatedChatbot.id ? updatedChatbot : c
      )
    );
    setShowFlowEditor(false);
    setEditingChatbotId(null);
  };

  // Filter chatbots based on search term and status
  const filteredChatbots = chatbots.filter(chatbot => {
    const matchesSearch = chatbot.chatbot_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chatbot.id.toString().includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && chatbot.isactive === 1) ||
      (statusFilter === 'inactive' && chatbot.isactive === 0);
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const chatbotDate = new Date(chatbot.created_at);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);
      const lastMonth = new Date(today);
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      switch (dateFilter) {
        case 'today':
          matchesDate = chatbotDate.toDateString() === today.toDateString();
          break;
        case 'yesterday':
          matchesDate = chatbotDate.toDateString() === yesterday.toDateString();
          break;
        case 'last7days':
          matchesDate = chatbotDate >= lastWeek;
          break;
        case 'last30days':
          matchesDate = chatbotDate >= lastMonth;
          break;
        default:
          matchesDate = true;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    return status === 1 ? (
      <span className="chatbot-status-badge chatbot-status-active">
        <FontAwesomeIcon icon={faCheckCircle} />
        Active
      </span>
    ) : (
      <span className="chatbot-status-badge chatbot-status-inactive">
        <FontAwesomeIcon icon={faClock} />
        Inactive
      </span>
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter('all');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (statusFilter !== 'all') count++;
    if (dateFilter !== 'all') count++;
    return count;
  };

  // Helper function to parse JSON strings safely
  const parseJsonSafely = (jsonString, defaultValue = []) => {
    if (!jsonString) return defaultValue;
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Error parsing JSON:', jsonString, error);
      return defaultValue;
    }
  };

  console.log('Current chatbots state:', chatbots);
  console.log('Filtered chatbots:', filteredChatbots);
  console.log('Loading state:', loading);

  if (loading) {
    return <Preloader action={loading} />;
  }

  // Check if user has permission to view chatbots

  return (
    <div className="chatbot-management-container">
      {/* Header Section */}
      <div className="chatbot-management-header">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="chatbot-management-title">
              Manage Chatbots
            </h4>
            <p className="chatbot-management-subtitle">
              Create, manage, and monitor your WhatsApp chatbots
            </p>
          </div>
          <div className="chatbot-stats">
            <div className="chatbot-stat-badge chatbot-stat-active">
              <FontAwesomeIcon icon={faCheckCircle} />
              {chatbots.filter(c => c.isactive === 1).length} Active
            </div>
            <div className="chatbot-stat-badge chatbot-stat-inactive">
              <FontAwesomeIcon icon={faClock} />
              {chatbots.filter(c => c.isactive === 0).length} Inactive
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters Section */}
      <div className="chatbot-filters-section">
        <div className="chatbot-filters-grid">
          {/* Search Input */}
          <div className="chatbot-search-container">
            <input
              type="text"
              className="chatbot-search-input"
              placeholder="Search by chatbot name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FontAwesomeIcon icon={faSearch} className="chatbot-search-icon" />
          </div>

          {/* Status Filter */}
          <select
            className="chatbot-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Date Filter */}
          <select
            className="chatbot-filter-select"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7days">Last 7 Days</option>
            <option value="last30days">Last 30 Days</option>
          </select>

          {/* Clear Filters Button */}
          <button
            className="chatbot-clear-btn"
            onClick={clearFilters}
            disabled={getActiveFiltersCount() === 0}
          >
            <FontAwesomeIcon icon={faTimes} />
            Clear
          </button>
        </div>

        {/* Results Summary */}
        <div className="chatbot-results-summary">
          <div>
            Showing {filteredChatbots.length} of {chatbots.length} chatbots
            {getActiveFiltersCount() > 0 && (
              <span className="ms-2">
                <span className="chatbot-filter-count">
                  {getActiveFiltersCount()} filter{getActiveFiltersCount() > 1 ? 's' : ''} active
                </span>
              </span>
            )}
          </div>
          <button
            className="chatbot-create-btn"
            onClick={handleCreateNew}
          >
            <FontAwesomeIcon icon={faPlus} />
            Create New Chatbot
          </button>
        </div>
      </div>
      
      {/* Table Section */}
      <div className="chatbot-table-container">
        <table className="chatbot-management-table">
          <thead>
            <tr>
              <th>Chatbot Name</th>
              <th>Status</th>
              <th>Trigger Keywords</th>
              <th>Created</th>
              <th>Updated</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredChatbots.length > 0 ? (
              filteredChatbots.map((chatbot) => (
                <tr key={chatbot.id}>
                  <td>
                    <div className="chatbot-name-cell">
                      <div className="chatbot-icon">
                        <FontAwesomeIcon icon={faRobot} />
                      </div>
                      <div className="chatbot-info">
                        <div className="chatbot-name">{chatbot.chatbot_name}</div>
                        <div className="chatbot-id">ID: #{chatbot.id}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {getStatusBadge(chatbot.isactive)}
                  </td>
                  <td>
                    <div className="chatbot-triggers">
                      {(() => {
                        const parsedTgm = parseJsonSafely(chatbot.tgm);
                        if (parsedTgm && Array.isArray(parsedTgm) && parsedTgm.length > 0) {
                          return (
                            <>
                              {parsedTgm.slice(0, 2).map((keyword, index) => (
                                <span key={index} className="chatbot-trigger-badge">
                                  {keyword}
                                </span>
                              ))}
                              {parsedTgm.length > 2 && (
                                <span className="chatbot-trigger-more">
                                  +{parsedTgm.length - 2} more
                                </span>
                              )}
                            </>
                          );
                        } else {
                          return <span className="chatbot-no-triggers">No keywords</span>;
                        }
                      })()}
                    </div>
                  </td>
                  <td className="chatbot-date">{formatDate(chatbot.created_at)}</td>
                  <td className="chatbot-date">{formatDate(chatbot.updated_at)}</td>
                  <td>
                    <div className="chatbot-actions">
                      <button
                        className="chatbot-action-btn chatbot-action-view"
                        onClick={() => handleViewDetails(chatbot)}
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className="chatbot-action-btn chatbot-action-edit"
                        onClick={() => handleEditChatbot(chatbot.id)}
                        title="Edit Chatbot"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="chatbot-action-btn chatbot-action-duplicate"
                        onClick={() => handleDuplicateChatbot(chatbot)}
                        title="Duplicate"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      <button
                        className="chatbot-action-btn chatbot-action-toggle"
                        onClick={() => handleToggleStatus(chatbot.id, chatbot.isactive)}
                        title={chatbot.isactive === 1 ? 'Deactivate' : 'Activate'}
                      >
                        <FontAwesomeIcon icon={chatbot.isactive === 1 ? faPause : faPlay} />
                      </button>
                      <button
                        className="chatbot-action-btn chatbot-action-delete"
                        onClick={() => handleDeleteChatbot(chatbot.id, chatbot.chatbot_name)}
                        disabled={loadingChatbotId === chatbot.id}
                        title="Delete"
                      >
                        {loadingChatbotId === chatbot.id ? (
                          <div className="chatbot-loading-spinner"></div>
                        ) : (
                          <FontAwesomeIcon icon={faTrash} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="chatbot-empty-state">
                  <div className="d-flex flex-column align-items-center">
                    <div className="chatbot-empty-icon">
                      <FontAwesomeIcon icon={faRobot} />
                    </div>
                    <h5 className="chatbot-empty-title">
                      {chatbots.length > 0 ? 'No Results Found' : 'No Chatbots Found'}
                    </h5>
                    <p className="chatbot-empty-message">
                      {chatbots.length > 0 
                        ? 'Try adjusting your search criteria or filters'
                        : 'Create your first chatbot to get started with automated conversations'
                      }
                    </p>
                    {chatbots.length === 0 && (
                      <button 
                        className="chatbot-create-first-btn"
                        onClick={handleCreateNew}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Create First Chatbot
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Chatbot Details Modal */}
      {showChatbotDetails && selectedChatbot && (
        <div className="chatbot-modal-overlay" onClick={() => setShowChatbotDetails(false)}>
          <div className="chatbot-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chatbot-modal-header">
              <h5 className="chatbot-modal-title">
                <FontAwesomeIcon icon={faRobot} className="me-2" />
                {selectedChatbot.chatbot_name}
              </h5>
              <button
                type="button"
                className="chatbot-modal-close"
                onClick={() => setShowChatbotDetails(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="chatbot-modal-body">
              <div className="chatbot-modal-grid">
                <div className="chatbot-modal-section">
                  <h6 className="chatbot-modal-section-title">Basic Information</h6>
                  <div className="chatbot-modal-info">
                    <div className="chatbot-modal-info-item">
                      <span className="chatbot-modal-label">ID:</span>
                      <span className="chatbot-modal-value">#{selectedChatbot.id}</span>
                    </div>
                    <div className="chatbot-modal-info-item">
                      <span className="chatbot-modal-label">Name:</span>
                      <span className="chatbot-modal-value">{selectedChatbot.chatbot_name}</span>
                    </div>
                    <div className="chatbot-modal-info-item">
                      <span className="chatbot-modal-label">Status:</span>
                      <span className="chatbot-modal-value">{getStatusBadge(selectedChatbot.isactive)}</span>
                    </div>
                    <div className="chatbot-modal-info-item">
                      <span className="chatbot-modal-label">Created:</span>
                      <span className="chatbot-modal-value">{formatDate(selectedChatbot.created_at)}</span>
                    </div>
                    <div className="chatbot-modal-info-item">
                      <span className="chatbot-modal-label">Updated:</span>
                      <span className="chatbot-modal-value">{formatDate(selectedChatbot.updated_at)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="chatbot-modal-section">
                  <h6 className="chatbot-modal-section-title">Trigger Keywords</h6>
                  <div className="chatbot-modal-triggers">
                    {(() => {
                      const parsedTgm = parseJsonSafely(selectedChatbot.tgm);
                      if (parsedTgm && Array.isArray(parsedTgm) && parsedTgm.length > 0) {
                        return parsedTgm.map((keyword, index) => (
                          <span key={index} className="chatbot-modal-trigger-badge">
                            {keyword}
                          </span>
                        ));
                      } else {
                        return <span className="chatbot-modal-no-triggers">No trigger keywords defined</span>;
                      }
                    })()}
                  </div>
                  
                  <h6 className="chatbot-modal-section-title mt-3">Flow Structure</h6>
                  <div className="chatbot-modal-flow">
                    {(() => {
                      const parsedNodes = parseJsonSafely(selectedChatbot.chatbot_node, {});
                      if (parsedNodes && typeof parsedNodes === 'object' && Object.keys(parsedNodes).length > 0) {
                        return Object.entries(parsedNodes).map(([keyword, nodes]) => (
                          <div key={keyword} className="chatbot-modal-flow-item">
                            <strong className="chatbot-modal-flow-keyword">"{keyword}" flow:</strong>
                            <div className="chatbot-modal-flow-nodes">
                              {Array.isArray(nodes) ? (
                                nodes.map((node, index) => (
                                  <div key={index} className="chatbot-modal-flow-node">
                                    {index + 1}. {node.type}: {node.message || 'No message'}
                                  </div>
                                ))
                              ) : (
                                <div className="chatbot-modal-flow-error">Invalid node structure</div>
                              )}
                            </div>
                          </div>
                        ));
                      } else {
                        return <span className="chatbot-modal-no-flow">No flow structure defined</span>;
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>
            <div className="chatbot-modal-footer">
              <button
                type="button"
                className="chatbot-modal-btn chatbot-modal-btn-secondary"
                onClick={() => setShowChatbotDetails(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="chatbot-modal-btn chatbot-modal-btn-primary"
                onClick={() => {
                  setShowChatbotDetails(false);
                  handleEditChatbot(selectedChatbot.id);
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
                Edit Chatbot
              </button>
            </div>
          </div>
                 </div>
       )}

       {/* Flow Editor */}
       {showFlowEditor && editingChatbotId && (
         <ChatbotFlowEditor
           chatbotId={editingChatbotId}
           onClose={handleFlowEditorClose}
           onSave={handleFlowEditorSave}
         />
       )}
     </div>
   );
 }

export default ManageChatbots;
